import React from "react";
import PropTypes from "prop-types";
import { makeStyles, shorthands, Button, mergeClasses } from "@fluentui/react-components";
import { Dismiss12Regular } from "@fluentui/react-icons";
import FileTypeIcon from "./FileTypeIcon";
import EmbeddingStatusIcon from "./EmbeddingStatusIcon";
import { EMBEDDING_FAILED, EMBEDDING_IN_PROCESS } from "../utils/resourceUtils";

const useStyles = makeStyles({
  chipsRow: {
    display: "flex",
    flexWrap: "wrap",
    alignSelf: "stretch",
    ...shorthands.gap("2px"),
    ...shorthands.padding("2px"),
    flexShrink: 0,
  },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    ...shorthands.gap("4px"),
    ...shorthands.padding("2px", "4px"),
    ...shorthands.borderRadius("12px"),
    ...shorthands.border("1px", "solid", "#357C7C"),
    backgroundColor: "rgba(53, 124, 124, 0.08)",
    maxWidth: "100%",
    minHeight: "22px",
  },
  chipFailed: {
    ...shorthands.borderColor("#C50F1F"),
    backgroundColor: "rgba(197, 15, 31, 0.08)",
  },
  chipInProcess: {
    ...shorthands.borderColor("#CA5010"),
    backgroundColor: "rgba(202, 80, 16, 0.08)",
  },
  docIconPdf: {
    width: "14px",
    height: "17px",
    flexShrink: 0,
    display: "block",
  },
  docIconWord: {
    width: "18px",
    height: "17px",
    flexShrink: 0,
    display: "block",
  },
  docIconDefault: {
    width: "14px",
    height: "14px",
    flexShrink: 0,
    color: "#569797",
  },
  chipText: {
    fontFamily: "Segoe UI",
    fontSize: "12px",
    color: "#333333",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: "160px",
    lineHeight: "16px",
  },
  chipStatusIcon: {
    flexShrink: 0,
  },
  chipDismiss: {
    minWidth: "18px",
    minHeight: "18px",
    ...shorthands.padding("0px"),
  },
});

const SelectedFileChips = ({ selectedFiles, removeFile, disabled }) => {
  const styles = useStyles();

  if (!selectedFiles.length) {
    return null;
  }

  return (
    <div className={styles.chipsRow}>
      {selectedFiles.map((file) => {
        const chipClass = mergeClasses(
          styles.chip,
          file.embedding_status === EMBEDDING_FAILED && styles.chipFailed,
          file.embedding_status === EMBEDDING_IN_PROCESS && styles.chipInProcess
        );

        return (
          <div key={file.connected_id} className={chipClass}>
            <FileTypeIcon
              fileName={file.name}
              pdfClassName={styles.docIconPdf}
              wordClassName={styles.docIconWord}
              defaultClassName={styles.docIconDefault}
            />
            <span className={styles.chipText} title={file.name}>
              {file.name}
            </span>
            <EmbeddingStatusIcon
              embeddingStatus={file.embedding_status}
              className={styles.chipStatusIcon}
            />
            <Button
              className={styles.chipDismiss}
              appearance="transparent"
              icon={<Dismiss12Regular />}
              aria-label={`Remove ${file.name}`}
              onClick={() => removeFile(file.connected_id)}
              disabled={disabled}
            />
          </div>
        );
      })}
    </div>
  );
};

SelectedFileChips.propTypes = {
  selectedFiles: PropTypes.array.isRequired,
  removeFile: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

export default SelectedFileChips;
